import { useParams, Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Mail } from "lucide-react";
import Layout from "@/components/Layout";
import ProductCard from "@/components/ProductCard";
import { findProduct, relatedProducts } from "@/lib/catalog";
import { useT } from "@/lib/i18n";

const ThankYouPage = () => {
  const t = useT();
  const { slug } = useParams();
  const product = findProduct(slug || "bordeaux");
  const related = relatedProducts(slug, 3);

  return (
    <Layout testId="page-thank-you">
      <section className="pt-20 md:pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="w-14 h-14 rounded-full bg-[color:var(--gv-pink)]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 size={28} className="text-[color:var(--gv-pink)]" />
          </div>
          <h1 className="font-display text-[40px] md:text-[58px] text-[color:var(--gv-ink)] mb-3">
            {t("thanks.title")} <span aria-hidden>🍷</span>
          </h1>
          <p className="text-[17px] text-[color:var(--gv-ink)]/70">{t("thanks.sub")}</p>
        </div>
      </section>

      {/* Access product */}
      {product && (
        <section className="pb-12">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 bg-[color:var(--gv-mist)] rounded-[6px] p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-3">
              <img src={product.image} alt={product.name} className="w-full rounded-[5px] border border-[color:var(--gv-line)]" />
            </div>
            <div className="md:col-span-9">
              <div className="eyebrow mb-2">{t("thanks.map_ready")}</div>
              <div className="font-display text-[22px] md:text-[28px] text-[color:var(--gv-ink)] mb-3">{product.name}</div>
              <p className="text-[14px] text-[color:var(--gv-ink)]/70 mb-5 max-w-lg">{product.valueLine}</p>
              <Link to={`/product/${product.slug}`} className="btn-primary" data-testid="thankyou-view-map">{t("cta.view_map")} <ArrowRight size={16} /></Link>
            </div>
          </div>
        </section>
      )}

      {/* Next steps */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Link to="/learn" className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-7 hover:border-[color:var(--gv-pink)] transition-colors">
            <div className="eyebrow mb-2">{t("nav.learn")}</div>
            <div className="font-display text-[22px] text-[color:var(--gv-ink)] mb-2">{t("thanks.understand_region")}</div>
            <div className="text-[13.5px] text-[color:var(--gv-ink)]/70 mb-4">{t("product.learn_this_region_text", { region: product?.region || "" })}</div>
            <span className="link-arrow text-[13.5px]">{t("cta.start_learning")} <ArrowRight size={14} /></span>
          </Link>
          <Link to="/play" className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-7 hover:border-[color:var(--gv-pink)] transition-colors">
            <div className="eyebrow mb-2">{t("nav.play")}</div>
            <div className="font-display text-[22px] text-[color:var(--gv-ink)] mb-2">{t("thanks.test_yourself")}</div>
            <div className="text-[13.5px] text-[color:var(--gv-ink)]/70 mb-4">{t("product.test_knowledge_text", { region: product?.region || "" })}</div>
            <span className="link-arrow text-[13.5px]">{t("cta.take_quiz")} <ArrowRight size={14} /></span>
          </Link>
        </div>
      </section>

      {/* Recommendations */}
      {related.length > 0 && (
        <section className="py-16 bg-[color:var(--gv-mist)]">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <h3 className="font-display text-[24px] md:text-[30px] text-[color:var(--gv-ink)] mb-6">{t("common.you_might_also")}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {related.map((p) => <ProductCard key={p.slug} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Summary + email */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-6">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-9 h-9 rounded-[5px] bg-[color:var(--gv-mist)] flex items-center justify-center">
                <Mail size={16} className="text-[color:var(--gv-blue)]" />
              </div>
              <div>
                <div className="font-display text-[17px] text-[color:var(--gv-ink)]">{t("thanks.order_confirmation")}</div>
                <div className="text-[12.5px] text-[color:var(--gv-ink)]/60">{t("thanks.receipt_sent")}</div>
              </div>
            </div>
            <div className="flex items-center justify-between text-[14px] border-t border-[color:var(--gv-line)] pt-4">
              <div className="text-[color:var(--gv-ink)]/75">{product?.name || "geoVINUM"}</div>
              <div className="font-medium text-[color:var(--gv-ink)]">€{product?.priceFrom || 25}</div>
            </div>
          </div>
          <div className="mt-6 text-center">
            <Link to="/dashboard" className="btn-secondary">{t("cta.go_dashboard")} <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ThankYouPage;
